import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Button, Table, Popconfirm, message } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined, PictureOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import axios from '../utils/axios';
import BreadCrumb from '../components/Breadcrumb';
import TextFilter from '../components/TextFilter';

function Events() {
	const [gridLoading, setGridLoading] = useState(true);
	const [gridData, setGridData] = useState([]);
	const [eventTypes, setEventTypes] = useState({});
	const [totalRecords, setTotalRecords] = useState(0);
	const [gridHeight, setGridHeight] = useState(window.innerWidth - 182);

	const get = (options = {}) => {
		if (!options.pagination) {
			options.pagination = { start: 0, limit: 20 };
		} else {
			options.pagination = {
				start: (options.pagination.current - 1) * options.pagination.pageSize,
				limit: options.pagination.pageSize,
			};
		}

		if (options.sorter && options.sorter.order) {
			options.sorter = {
				field: options.sorter.field,
				order: options.sorter.order,
			};
		} else {
			options.sorter = { field: '_id', order: 'desc' };
		}

		if (options.filters) {
			// options.filters = _.omitBy(options.filters, _.isNil);
		}

		axios
			.all([axios.get(`/event`, { params: options }), axios.get(`/event-type`)])
			.then(
				axios.spread((result1, result2) => {
					const eventTypesData = {};
					if (result2?.data?.data?.length) {
						result2.data.data.map(({ _id, eventType }) => {
							eventTypesData[_id] = eventType;
							return eventType;
						});
					}
					setEventTypes(eventTypesData);
					setGridData(result1.data.data);
					setTotalRecords(result1.data.data.length);
				})
			)
			.finally(() => {
				setGridLoading(false);
			});
	};

	const deleteRecord = (record) => {
		setGridLoading(true);
		axios
			.delete(`/event`, { data: { _id: record._id } })
			.then((result) => {
				message.success(result.data.message);
				get();
			})
			.catch(() => {
				message.error('Oops! Something went wrong!');
			})
			.finally(() => {
				setGridLoading(false);
			});
	};

	const duplicateRecord = (record) => {
		setGridLoading(true);
		axios
			.post(`/event/duplicate`, { data: { _id: record._id } })
			.then((result) => {
				message.success(result.data.message);
				get();
			})
			.catch(() => {
				message.error('Oops! Something went wrong!');
			})
			.finally(() => {
				setGridLoading(false);
			});
	};

	const onTableChange = (pagination, filters, sorter) => {
		get({ pagination, filters, sorter });
	};

	useEffect(() => {
		get();
		window.onresize = () => {
			setGridHeight(window.innerWidth - 182);
		};
	}, []);

	return (
		<>
			<BreadCrumb
				breadcrumbs={[
					{
						link: '/',
						label: 'Home',
					},
					{
						label: 'Events',
					},
				]}
			/>
			<div className='main-content'>
				<div className='page-header'>
					<div className='page-title'>Events</div>
					<div className='page-title-more'>
						<Input.Search placeholder='Search' style={{ width: '140px' }} />
						<Link to='/events/add'>
							<Button icon={<PlusCircleOutlined />} type='primary'>
								Add
							</Button>
						</Link>
					</div>
				</div>
				<div className='grid-content'>
					<Table
						tableLayout='fixed'
						bordered
						size='small'
						rowKey='_id'
						showSorterTooltip={false}
						loading={gridLoading}
						dataSource={gridData}
						scroll={{ x: 850, y: gridHeight }}
						onChange={onTableChange}
						pagination={{
							defaultPageSize: 20,
							showLessItems: true,
							showQuickJumper: true,
							showSizeChanger: true,
							pageSizeOptions: [20, 50, 100, 200],
							size: 'small',
							responsive: true,
							total: totalRecords,
							showTotal: (total) => `Total ${total} items`,
						}}
						columns={[
							{
								title: 'Action',
								key: 'action',
								align: 'center',
								fixed: 'left',
								width: 104,
								render: (text, record, index) => (
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-around',
											alignItems: 'center',
										}}
									>
										<Link to={`/events/edit/${record._id}`}>
											<EditOutlined className='grid-edit-icon' title='Edit' />
										</Link>
										<Popconfirm
											title='Are you sure you want to duplicate this record?'
											okText='Yes'
											cancelText='No'
											size='small'
											placement='bottomLeft'
											onConfirm={() => {
												duplicateRecord(record);
											}}
										>
											<CopyOutlined style={{ fontSize: 14 }} title='Duplicate' />
										</Popconfirm>
										<Popconfirm
											title='Are you sure you want to delete this record?'
											okText='Yes'
											cancelText='No'
											size='small'
											placement='bottomLeft'
											onConfirm={() => {
												deleteRecord(record);
											}}
										>
											<DeleteOutlined style={{ fontSize: 14 }} title='Delete' />
										</Popconfirm>
									</div>
								),
							},
							{
								title: <PictureOutlined />,
								align: 'center',
								dataIndex: 'media',
								width: 72,
								render: (text, record) => {
									if (record.media?.length) {
										return <img src={`${record.media[0].mediaPath}`} alt={record.media[0].mediaTitle} style={{ height: 32 }} />;
									}
									return '';
								},
							},
							{
								title: 'Title',
								dataIndex: 'title',
								sorter: true,
								ellipsis: true,
								width: 200,
								filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => <TextFilter />,
								filterIcon: <SearchOutlined />,
							},
							{
								title: 'Type',
								dataIndex: 'eventType',
								ellipsis: true,
								width: 170,
								render: (text, record, index) => {
									const recordEventTypes = [];
									if (Array.isArray(record.eventType) && record.eventType.length) {
										record.eventType.map((eventType) => {
											if (eventTypes[eventType]) {
												recordEventTypes.push(eventTypes[eventType]);
											}
											return eventType;
										});
									}
									return recordEventTypes.join(', ');
								},
							},
							{
								title: 'Upcoming Batches',
								dataIndex: 'batches',
								ellipsis: true,
								width: 220,
								render: (text, record, index) => {
									const recordBatchesDates = [];
									if (Array.isArray(record.batches) && record.batches.length) {
										record.batches.map(({ batchStartsAt }) => {
											if (new Date(batchStartsAt) >= new Date()) {
												recordBatchesDates.push(Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' }).format(new Date(batchStartsAt)));
											}
											return batchStartsAt;
										});
									}
									return recordBatchesDates.join(', ');
								},
							},
							{
								title: 'Description',
								dataIndex: 'description',
								ellipsis: true,
							},
						]}
					/>
				</div>
			</div>
		</>
	);
}

export default Events;
